import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { catchError, last, of, Subscription, switchMap } from 'rxjs';
import type {
  AssessmentDescriptionSuggestion,
  AssessmentDescriptionSuggestionRequest,
} from '../../models/httpApi.model';
import { OrgManagementApi } from '../org-management-api/org-management-api';
import { pollUntil } from '../http/poll-until';

/** Scoped to one wizard. Owns cancellable draft jobs; never creates or updates an assessment. */
@Injectable()
export class AssessmentDescriptionSuggestionService {
  private readonly api = inject(OrgManagementApi);
  private readonly destroyRef = inject(DestroyRef);
  private subscription: Subscription | null = null;
  private contextKey: string | null = null;
  private context: { orgId: string; request: AssessmentDescriptionSuggestionRequest } | null = null;

  private readonly _state = signal<'idle' | 'loading' | 'settled' | 'failed'>('idle');
  readonly state = this._state.asReadonly();
  private readonly _result = signal<AssessmentDescriptionSuggestion | null>(null);
  readonly result = this._result.asReadonly();

  constructor() {
    this.destroyRef.onDestroy(() => this.reset());
  }

  /** Invalidate results as soon as their grounding changes, even while revisiting earlier steps. */
  setContext(orgId: string, request: AssessmentDescriptionSuggestionRequest): boolean {
    const contextKey = JSON.stringify([orgId, request]);
    if (contextKey === this.contextKey) return false;
    this.reset();
    this.contextKey = contextKey;
    this.context = { orgId, request };
    return true;
  }

  load(retry = false): void {
    if (!this.context || this.state() === 'loading' || (!retry && this.state() !== 'idle')) return;
    const { orgId, request } = this.context;
    if (!request.activity_id && request.context_document_ids.length === 0) {
      this._state.set('settled');
      return;
    }
    this.subscription?.unsubscribe();
    this._result.set(null);
    this._state.set('loading');
    type PollResult = AssessmentDescriptionSuggestion | undefined;
    this.subscription = this.api
      .startAssessmentDescriptionSuggestion(orgId, request, crypto.randomUUID())
      .pipe(
        switchMap(result =>
          result.status !== 'pending'
            ? of(result)
            : pollUntil<PollResult>(
                () =>
                  this.api
                    .getAssessmentDescriptionSuggestion(orgId, result.suggestion_id)
                    .pipe(catchError(() => of(undefined))),
                snapshot => !!snapshot && snapshot.status !== 'pending',
                { initialDelayMs: 2_000, intervalMs: 3_000, maxDurationMs: 185_000 }
              ).pipe(last(undefined, undefined))
        )
      )
      .subscribe({
        next: result => {
          this._result.set(result ?? null);
          this._state.set(result?.status === 'completed' ? 'settled' : 'failed');
        },
        error: () => this._state.set('failed'),
      });
  }

  reset(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.contextKey = null;
    this.context = null;
    this._result.set(null);
    this._state.set('idle');
  }
}
